// Erişim çekmecesi — kırılım gizleme uyarısı (SAF; spec §6.5 DR10, §8 K3). Portal › Kullanıcılar
// çekmecesi ve danışman › KPI erişimi editörü ortak (AccessSetEditor). Kural sunucuyla aynı
// (`applyBreakdownVisibility`): kırılımlı toplamın HERHANGİ bir grubundaki bir üye gizliyse toplam
// müşteriye gizlenir; diğer üyeler görünür kalırsa toplam onlardan türetilebilir (aritmetik sızıntı
// bilinçli kabul — yönetici uyarılır, [Diğer kalemleri de gizle] kısayolu). Toplamı kullanan
// hesaplanan göstergeler (V7: görünür kalır) uyarıda adıyla listelenir: formülde toplamın kodu ya
// da toplamı kapsayan kategori (`TOPLA/ORT/AGIRLIKLI_ORT(<kategori>)`) — sunucu `derivedInputIds`'i
// shared `parseFormula` ile kurar; burada yalnız kategori ataları eklenir.
// Satır türü TEK kaynak: shared `rowKindsOf` (`effectiveNodeKind`).

import { rowKindsOf, type ClientAdminSet, type KpiRowKind } from "@mahir/shared";
import { effectiveLevel, setOverride, type SetDraftV2 } from "./client-access-draft-v2";

export interface BreakdownTotalShape {
  totalId: string;
  label: string;
  /** tüm gruplardaki üye satırı kimlikleri (grup sırasıyla) */
  memberIds: string[];
}

/** Setin kırılımlı toplamları + üyeleri (yalnız DTO'da görünen düğümler). */
export function breakdownTotals(set: ClientAdminSet): BreakdownTotalShape[] {
  const kinds = rowKindsOf(set.indicators.map((i, orderIndex) => ({ ...i, orderIndex })));
  const childrenOf = (id: string): string[] =>
    set.indicators.filter((i) => i.parentId === id).map((i) => i.id);
  const out: BreakdownTotalShape[] = [];
  for (const t of set.indicators) {
    if (kinds.get(t.id) !== "total") continue;
    const groups = childrenOf(t.id).filter((g) => kinds.get(g) === "breakdown_group");
    out.push({ totalId: t.id, label: t.label, memberIds: groups.flatMap(childrenOf) });
  }
  return out;
}

/** Taslakta müşteriye gizli mi (danışman tavanı dahil; rol readonly yapar, gizlemez). */
function hidden(set: ClientAdminSet, d: SetDraftV2, id: string): boolean {
  return effectiveLevel(set, d, id) === "closed";
}

/**
 * Sabit nokta (sunucu `applyBreakdownVisibility` ile aynı): üyesi gizli olan kırılımlı toplam da
 * gizlidir; iki seviyede alt üye gizli → üye-toplam (Mavi yaka) gizli → kök toplam gizli.
 */
function hiddenClosure(set: ClientAdminSet, d: SetDraftV2): Set<string> {
  const closed = new Set(set.indicators.filter((i) => hidden(set, d, i.id)).map((i) => i.id));
  const totals = breakdownTotals(set);
  for (let changed = true; changed; ) {
    changed = false;
    for (const t of totals) {
      if (closed.has(t.totalId)) continue;
      if (t.memberIds.some((id) => closed.has(id))) {
        closed.add(t.totalId);
        changed = true;
      }
    }
  }
  return closed;
}

function kindMap(set: ClientAdminSet): Map<string, KpiRowKind> {
  return rowKindsOf(set.indicators.map((i, orderIndex) => ({ ...i, orderIndex })));
}

/**
 * Toplamı kullanan, taslakta görünür hesaplanan göstergelerin etiketleri: girdisi toplamın kendisi
 * ya da toplamı kapsayan bir kategori (ata) olan türetilmişler. Toplamın kendisi ve üyeleri hariç.
 */
export function breakdownDependents(
  set: ClientAdminSet,
  d: SetDraftV2,
  totalId: string,
  kinds: Map<string, KpiRowKind> = kindMap(set),
): string[] {
  const byId = new Map(set.indicators.map((i) => [i.id, i]));
  const refs = new Set<string>([totalId]);
  const seen = new Set<string>([totalId]);
  let cur = byId.get(totalId)?.parentId ?? null;
  while (cur && !seen.has(cur)) {
    seen.add(cur);
    if (kinds.get(cur) === "category") refs.add(cur);
    cur = byId.get(cur)?.parentId ?? null;
  }
  return set.indicators
    .filter(
      (i) =>
        i.id !== totalId &&
        i.isDerived &&
        i.derivedInputIds.some((x) => refs.has(x)) &&
        !hidden(set, d, i.id),
    )
    .map((i) => i.label);
}

export interface BreakdownHideWarning {
  totalId: string;
  totalLabel: string;
  /** gizlenen üyelerin etiketleri */
  hiddenLabels: string[];
  /** görünür kalan üye kimlikleri — [Diğer kalemleri de gizle] bunları gizler */
  visibleMemberIds: string[];
  /** toplamı kullanan görünür hesaplanan göstergeler */
  dependents: string[];
  /** aynı gizleme kümesinde onay tekrar sorulmasın (`toplam:gizli1,gizli2`) */
  key: string;
}

/**
 * Taslakta kendisi açık (kategori/istisna seviyesi gizli değil) olan ama en az bir üyesi gizli ve
 * en az bir üyesi görünür kırılımlı toplamlar. Tüm üyeler gizliyse sızıntı yok → uyarı yok.
 */
export function breakdownHideWarnings(set: ClientAdminSet, d: SetDraftV2): BreakdownHideWarning[] {
  const kinds = kindMap(set);
  const byId = new Map(set.indicators.map((i) => [i.id, i]));
  const closed = hiddenClosure(set, d);
  const out: BreakdownHideWarning[] = [];
  for (const t of breakdownTotals(set)) {
    if (hidden(set, d, t.totalId)) continue;
    const hiddenIds = t.memberIds.filter((id) => closed.has(id));
    const visible = t.memberIds.filter((id) => !closed.has(id));
    if (hiddenIds.length === 0 || visible.length === 0) continue;
    out.push({
      totalId: t.totalId,
      totalLabel: t.label,
      hiddenLabels: hiddenIds.map((id) => byId.get(id)?.label ?? ""),
      visibleMemberIds: visible,
      dependents: breakdownDependents(set, d, t.totalId, kinds),
      key: `${t.totalId}:${hiddenIds.join(",")}`,
    });
  }
  return out;
}

/** [Diğer kalemleri de gizle] — toplamın (tüm gruplarındaki) görünür üyeleri istisnayla gizlenir. */
export function hideOtherMembers(set: ClientAdminSet, d: SetDraftV2, totalId: string): SetDraftV2 {
  const totals = breakdownTotals(set);
  if (!totals.some((t) => t.totalId === totalId)) return d;
  // çocuklu düğüme istisna yazılmaz (422 override_on_category — sunucu, yalnız grup çocuklu üye-toplamı
  // kategori saymaz ama istisna yine de yaprak üyelere yazılır: güvenli seçim); üye-toplam yerine yaprak üyeleri
  const leaves = (id: string, seen: Set<string>): string[] => {
    const t = totals.find((x) => x.totalId === id);
    if (!t || seen.has(id)) return [id];
    seen.add(id);
    return t.memberIds.flatMap((m) => leaves(m, seen));
  };
  let next = d;
  for (const id of leaves(totalId, new Set()).filter((x) => x !== totalId)) {
    if (!hidden(set, next, id)) next = setOverride(next, id, "closed");
  }
  return next;
}

/** Kategori listesinde toplam satırının ipucu: bir üyesi gizli olduğu için toplam gizlenir. */
export function totalHiddenByMember(set: ClientAdminSet, d: SetDraftV2, totalId: string): boolean {
  const t = breakdownTotals(set).find((x) => x.totalId === totalId);
  const closed = hiddenClosure(set, d);
  return !!t && !hidden(set, d, totalId) && t.memberIds.some((id) => closed.has(id));
}
